import { DocumentationFilterModel } from 'shared/domain/documentation/filtering/matchesDocumentationFilter';
import { debugLog } from 'shared/logger/debugLog';
import {
  DocumentationFilteringRead,
  DocumentationFilteringSnapshotCacheEntry,
  FilteredDirectoryCacheKeyInput,
  FilteredDirectoryQueryCacheEntry,
  FilteredDirectoryResponse,
} from './types';

const FILTERED_DIRECTORY_QUERY_TTL_MS = 5000;
const DOCUMENTATION_SNAPSHOT_TTL_MS = 60000;

/**
 * Diagnostics of the cache in the worker's console (not on production): hits,
 * computations and what dropped the caches.
 */
export function logDocumentationFiltering(event: string, details: Record<string, unknown> = {}): void {
  debugLog(`[documentation filtering] ${event}`, details);
}

/**
 * Cached filtering of the documentation module, kept in the worker's memory.
 * - The answer for a directory with given filters is kept 5 s: the page asks for
 *   the same directory several times in a row (opening, URL sync, refresh after an
 *   event), and the filtering would run over IndexedDB each time.
 * - All files and directories are kept 60 s as a snapshot: a filter matches files
 *   deep in subdirectories, so the whole tree is read for it, which on a large
 *   project (thousands of files) costs far more than reading one directory.
 * The files and subdirectories of the current directory itself are always read fresh.
 * Every change of the data drops what it affects: a file's change its directories,
 * a directory change, a finished sync of files or directories and a clear drop all.
 */
export class DocumentationFilteringCacheManager {
  private documentationFilteringSnapshotCache: DocumentationFilteringSnapshotCacheEntry | null =
    null;

  private readonly filteredDirectoryQueryCache = new Map<
    string,
    FilteredDirectoryQueryCacheEntry
  >();

  /** Bumped by every invalidation: a read that started before it caches nothing. */
  private generation = 0;

  /** Starts a read: what it computes is cached only if the caches are not dropped meanwhile. */
  public startRead(): DocumentationFilteringRead {
    return { generation: this.generation };
  }

  /** Whether no invalidation came since the read started. */
  public isCurrent({ generation }: DocumentationFilteringRead): boolean {
    return generation === this.generation;
  }

  public getFilteredDirectoryResponseFromCache(
    keyInput: FilteredDirectoryCacheKeyInput
  ): FilteredDirectoryResponse | null {
    const now = Date.now();
    this.pruneExpiredFilteredDirectoryQueryCache(now);

    const cacheKey = this.buildFilteredDirectoryCacheKey(keyInput);
    const cacheEntry = this.filteredDirectoryQueryCache.get(cacheKey);

    if (!cacheEntry) {
      return null;
    }

    if (now - cacheEntry.createdAt > FILTERED_DIRECTORY_QUERY_TTL_MS) {
      this.filteredDirectoryQueryCache.delete(cacheKey);
      return null;
    }

    return cacheEntry.response;
  }

  /** Caches the answer of a read; skipped (`false`) when the data changed while it was computed. */
  public setFilteredDirectoryResponseCache(
    keyInput: FilteredDirectoryCacheKeyInput,
    response: FilteredDirectoryResponse,
    read: DocumentationFilteringRead
  ): boolean {
    if (!this.isCurrent(read)) {
      return false;
    }
    const now = Date.now();
    this.pruneExpiredFilteredDirectoryQueryCache(now);

    const cacheKey = this.buildFilteredDirectoryCacheKey(keyInput);
    this.filteredDirectoryQueryCache.set(cacheKey, {
      response,
      parentId: keyInput.parentId,
      createdAt: now,
    });
    return true;
  }

  public getDocumentationFilteringSnapshots(): {
    allDocumentations: DocumentationFilteringSnapshotCacheEntry['allDocumentations'];
    allDirectories: DocumentationFilteringSnapshotCacheEntry['allDirectories'];
  } | null {
    if (!this.documentationFilteringSnapshotCache) {
      return null;
    }

    const now = Date.now();
    const isExpired =
      now - this.documentationFilteringSnapshotCache.createdAt >
      DOCUMENTATION_SNAPSHOT_TTL_MS;

    if (this.documentationFilteringSnapshotCache.dirty || isExpired) {
      return null;
    }

    return {
      allDocumentations: this.documentationFilteringSnapshotCache.allDocumentations,
      allDirectories: this.documentationFilteringSnapshotCache.allDirectories,
    };
  }

  /** Caches the snapshot a read took; skipped (`false`) when the data changed while it was read. */
  public setDocumentationFilteringSnapshots(
    allDocumentations: DocumentationFilteringSnapshotCacheEntry['allDocumentations'],
    allDirectories: DocumentationFilteringSnapshotCacheEntry['allDirectories'],
    read: DocumentationFilteringRead
  ): boolean {
    if (!this.isCurrent(read)) {
      return false;
    }
    this.documentationFilteringSnapshotCache = {
      allDocumentations,
      allDirectories,
      createdAt: Date.now(),
      dirty: false,
    };
    return true;
  }

  public invalidateAllDocumentationFilteringCaches(): void {
    this.generation += 1;
    this.documentationFilteringSnapshotCache = null;
    this.filteredDirectoryQueryCache.clear();
    logDocumentationFiltering('invalidated all');
  }

  public invalidateDocumentationFilteringCachesByParentIds(
    parentIds: string[]
  ): void {
    if (parentIds.length === 0) {
      this.invalidateAllDocumentationFilteringCaches();
      return;
    }

    this.generation += 1;
    logDocumentationFiltering('invalidated directories', { parentIds });
    const parentIdsSet = new Set(parentIds.filter((id) => Boolean(id)));

    for (const [cacheKey, cacheEntry] of this.filteredDirectoryQueryCache) {
      if (parentIdsSet.has(cacheEntry.parentId)) {
        this.filteredDirectoryQueryCache.delete(cacheKey);
      }
    }

    if (this.documentationFilteringSnapshotCache) {
      this.documentationFilteringSnapshotCache = {
        ...this.documentationFilteringSnapshotCache,
        dirty: true,
      };
    }
  }

  private normalizeFilterModel(
    filterModel: DocumentationFilterModel
  ): DocumentationFilterModel {
    const items = (filterModel.items || []).map((item) => ({
      field: item.field || '',
      operator: item.operator || '',
      value: item.value ?? null,
    }));

    items.sort((a, b) => {
      const left = `${a.field}|${a.operator}|${JSON.stringify(a.value)}`;
      const right = `${b.field}|${b.operator}|${JSON.stringify(b.value)}`;
      return left.localeCompare(right);
    });

    return {
      logicOperator: filterModel.logicOperator || 'and',
      items,
    };
  }

  private buildFilteredDirectoryCacheKey(
    input: FilteredDirectoryCacheKeyInput
  ): string {
    const normalizedFilterModel = this.normalizeFilterModel(
      input.filterModel || { items: [] }
    );

    return JSON.stringify({
      parentId: input.parentId,
      documentState: input.documentState,
      timezone: input.timezone,
      organizationId: input.organizationId,
      filterModel: normalizedFilterModel,
    });
  }

  private pruneExpiredFilteredDirectoryQueryCache(now: number): void {
    for (const [cacheKey, cacheEntry] of this.filteredDirectoryQueryCache) {
      if (now - cacheEntry.createdAt > FILTERED_DIRECTORY_QUERY_TTL_MS) {
        this.filteredDirectoryQueryCache.delete(cacheKey);
      }
    }
  }
}

export const documentationFilteringCacheManager =
  new DocumentationFilteringCacheManager();
